import React, { useState, useEffect, useReducer } from 'react';
import { useParams, Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { Box, Heading, Text, Input, Button, useColorModeValue, Badge, HStack, VStack, IconButton, Tooltip, Checkbox, useDisclosure} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon, CopyIcon, DeleteIcon as DeleteBulkIcon, ViewIcon } from '@chakra-ui/icons';
import EditPollForm from './ManageQuestions/EditPollForm';
import EditQuestionForm from './ManageQuestions/EditQuestionForm';
import EditChoiceForm from './ManageQuestions/EditChoiceForm';
import { useConfirmDialog } from './shared/ConfirmDialog';
import { useToast } from '../hooks/useToast';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import LoadingSpinner from './shared/LoadingSpinner';
import EmptyState from './shared/EmptyState';
import PageHeader from './shared/PageHeader';
import SurveyPreview from './SurveyPreview';
import PreviewPollModal from './PreviewPollModal';
import SurveyPageDesigner from './SurveyPageDesigner';
import ResponseTemplates from './ResponseTemplates';
import PreflightModal from './PreflightModal';
import getCSRFtoken from '../utils/getCSRFtoken';
import { buttonStyles, spacing, getStateBadgeColor } from '../theme/designSystem';

// Reducer for managing edit state
const editReducer = (state, action) => {
    switch (action.type) {
        case 'START_EDIT_POLL':
            return { ...state, editingPoll: true };
        case 'CANCEL_EDIT_POLL':
            return { ...state, editingPoll: false };
        case 'START_EDIT_QUESTION':
            return { ...state, editingQuestionId: action.payload };
        case 'CANCEL_EDIT_QUESTION':
            return { ...state, editingQuestionId: null };
        case 'START_EDIT_CHOICE':
            return { ...state, editingChoiceId: action.payload };
        case 'CANCEL_EDIT_CHOICE':
            return { ...state, editingChoiceId: null };
        default:
            return state;
    }
};

function ManageQuestions() {
    const { pollId } = useParams();
    const navigate = useNavigate();
    const [poll, setPoll] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [newQuestion, setNewQuestion] = useState('');
    const [newChoice, setNewChoice] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const bgColor = useColorModeValue('background.shade1', 'background.shade3');

    // Use reducer for edit state management
    const [editState, dispatch] = useReducer(editReducer, {
        editingPoll: false,
        editingQuestionId: null,
        editingChoiceId: null,
    });

    const { confirm, ConfirmDialogComponent } = useConfirmDialog();
    const toast = useToast();
    const { isOpen: isPreviewOpen, onClose: onPreviewClose } = useDisclosure();
    const { isOpen: isFullPreviewOpen, onClose: onFullPreviewClose } = useDisclosure();
    const { isOpen: isTemplatesOpen, onOpen: onTemplatesOpen, onClose: onTemplatesClose } = useDisclosure();
    const [templateQuestionId, setTemplateQuestionId] = useState(null);

    // Keyboard shortcuts
    useKeyboardShortcuts({
        'ctrl+p': (e) => {
            if (questions.length > 0) {
                window.open(`/preview-poll/${pollId}`, '_blank', 'width=500,height=900,scrollbars=yes');
            }
        },
        'ctrl+n': () => {
            document.querySelector('input[placeholder="New question text..."]')?.focus();
        },
        'escape': () => {
            if (editState.editingQuestionId) {
                dispatch({ type: 'CANCEL_EDIT_QUESTION' });
            } else if (editState.editingChoiceId) {
                dispatch({ type: 'CANCEL_EDIT_CHOICE' });
            } else if (editState.editingPoll) {
                dispatch({ type: 'CANCEL_EDIT_POLL' });
            }
        }
    });

    useEffect(() => {
        fetch(`/api/polls/${pollId}/`, {
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => setPoll(data))
            .catch(error => {
                toast.showError('Error', 'Failed to load poll details');
            });
    }, [pollId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetch(`/api/questions/?poll=${pollId}`, {
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => {
                // Ensure all questions have choices arrays
                const questions = (data.results || []).map(q => ({
                    ...q,
                    choices: q.choices || []
                }));
                setQuestions(questions);
            })
            .catch(error => {
                console.error('Load questions error:', error);
                toast.showError('Error', 'Failed to load questions');
            });
    }, [pollId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddQuestion = () => {
        if (!newQuestion.trim()) {
            toast.showError('Validation Error', 'Question text is required');
            return;
        }
        if (newQuestion.trim().length < 3) {
            toast.showError('Validation Error', 'Question must be at least 3 characters');
            return;
        }

        setSubmitting(true);
        fetch(`/api/questions/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFtoken(),
            },
            body: JSON.stringify({
                text: newQuestion,
                poll: pollId
            })
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            // Ensure choices array exists
            if (!data.choices) {
                data.choices = [];
            }
            setQuestions(prevQuestions => [...prevQuestions, data]);
            setNewQuestion('');
            toast.showSuccess('Success', 'Question added');
        })
        .catch(error => {
            console.error('Add question error:', error);
            toast.showError('Error', 'Failed to add question. Please try again.');
        })
        .finally(() => setSubmitting(false));
    };

    const handleAddChoice = (questionId) => {
        if (!newChoice.trim()) {
            toast.showError('Validation Error', 'Choice text is required');
            return;
        }

        let order = 1;
        const selectedQuestion = questions.find(question => question.id === questionId);
        if (selectedQuestion) {
            const highestOrder = selectedQuestion.choices.reduce((maxOrder, choice) => {
                return Math.max(maxOrder, choice.order);
            }, 0);
            order = highestOrder + 1;
        }

        fetch(`/api/choices/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFtoken(),
            },
            body: JSON.stringify({
                text: newChoice,
                question: questionId,
                order: order,
            })
        })
        .then(response => response.json())
        .then(data => {
            const updatedQuestions = questions.map(question => {
                if (question.id === questionId) {
                    return {
                        ...question,
                        choices: [...question.choices, data]
                    };
                }
                return question;
            });
            setQuestions(updatedQuestions);
            setNewChoice('');  // Clear the input field
        })
        .catch(error => {
            toast.showError('Error', 'Failed to add choice. Please try again.');
        });
    };

    const handleApplyTemplate = async (templateChoices) => {
        if (!templateQuestionId) return;

        try {
            const selectedQuestion = questions.find(q => q.id === templateQuestionId);
            const startingOrder = selectedQuestion?.choices?.length || 0;

            const newChoices = [];
            for (let i = 0; i < templateChoices.length; i++) {
                const response = await fetch(`/api/choices/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCSRFtoken(),
                    },
                    body: JSON.stringify({
                        text: templateChoices[i],
                        question: templateQuestionId,
                        order: startingOrder + i + 1,
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    newChoices.push(data);
                }
            }

            // Update local state with all new choices
            const updatedQuestions = questions.map(question => {
                if (question.id === templateQuestionId) {
                    return {
                        ...question,
                        choices: [...question.choices, ...newChoices]
                    };
                }
                return question;
            });
            setQuestions(updatedQuestions);
            toast.showSuccess('Success', `Added ${newChoices.length} choices from template`);
        } catch (error) {
            toast.showError('Error', 'Failed to apply template. Please try again.');
        }
    };

    const handleSavePoll = (updatedData) => {
        fetch(`/api/polls/${pollId}/`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFtoken(),
            },
            body: JSON.stringify(updatedData)
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error("Failed to update poll");
            }
        })
        .then(data => {
            setPoll(data);
            dispatch({ type: 'CANCEL_EDIT_POLL' });
        })
        .catch(error => {
            toast.showError('Error', 'Failed to update poll. Please try again.');
        });
    };

    const { isOpen: isPreflightOpen, onOpen: onPreflightOpen, onClose: onPreflightClose } = useDisclosure();

    const handleSetPollLive = () => {
        onPreflightOpen();
    };

    const handleCompletePoll = () => {
        confirm({
            title: 'Complete Poll',
            message: `Are you sure you want to complete "${poll.title}"? This will close the poll and prevent further responses.`,
            confirmText: 'Complete Poll',
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/polls/${pollId}/complete/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRFToken': getCSRFtoken(),
                        },
                        credentials: 'include',
                    });

                    if (!response.ok) {
                        throw new Error('Failed to complete poll');
                    }

                    const data = await response.json();
                    setPoll(data.poll);
                    toast.showSuccess('Success', 'Poll has been completed');
                } catch (error) {
                    toast.showError('Error', 'Failed to complete poll. Please try again.');
                }
            }
        });
    };


    const handleSaveQuestion = (questionId, updatedData) => {
        fetch(`/api/questions/${questionId}/`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFtoken(),
            },
            body: JSON.stringify(updatedData)
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error("Failed to update question");
            }
        })
        .then(data => {
            setQuestions(questions.map(q => q.id === questionId ? data : q));
            dispatch({ type: 'CANCEL_EDIT_QUESTION' });
            toast.showSuccess('Success', 'Question updated');
        })
        .catch(error => {
            toast.showError('Error', 'Failed to update question. Please try again.');
        });
    };

    const handleSaveChoice = (choiceId, updatedData) => {
        fetch(`/api/choices/${choiceId}/`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFtoken(),
            },
            body: JSON.stringify(updatedData)
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error("Failed to update choice");
            }
        })
        .then(data => {
            setQuestions(questions.map(question => ({
                ...question,
                choices: question.choices.map(choice => choice.id === choiceId ? data : choice)
            })));
            dispatch({ type: 'CANCEL_EDIT_CHOICE' });
            toast.showSuccess('Success', 'Choice updated');
        })
        .catch(error => {
            toast.showError('Error', 'Failed to update choice. Please try again.');
        });
    };

    const handleDeleteQuestion = (questionId) => {
        confirm({
            title: 'Delete Question',
            message: 'Are you sure you want to delete this question? This action cannot be undone.',
            confirmText: 'Delete',
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/questions/${questionId}/`, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'X-CSRFToken': getCSRFtoken(),
                        },
                    });

                    if (response.ok) {
                        const updatedQuestions = questions.filter(q => q.id !== questionId);
                        setQuestions(updatedQuestions);
                        toast.showSuccess('Success', 'Question deleted');
                    } else {
                        throw new Error('Failed to delete question');
                    }
                } catch (error) {
                    toast.showError('Error', 'Failed to delete question. Please try again.');
                }
            }
        });
    };

    const handleDeletePoll = () => {
        confirm({
            title: 'Delete Poll',
            message: 'Are you sure you want to delete this entire poll? This will delete all questions and choices. This action cannot be undone.',
            confirmText: 'Delete Poll',
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/polls/${pollId}/`, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'X-CSRFToken': getCSRFtoken(),
                        },
                    });

                    if (response.ok) {
                        toast.showSuccess('Success', 'Poll deleted successfully');
                        navigate('/pollsPage');
                    } else {
                        throw new Error('Failed to delete poll');
                    }
                } catch (error) {
                    toast.showError('Error', 'Failed to delete poll');
                }
            }
        });
    };

    const moveQuestion = async (questionId, direction) => {
        const currentIndex = questions.findIndex(q => q.id === questionId);
        if (
            (direction === 'up' && currentIndex === 0) ||
            (direction === 'down' && currentIndex === questions.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        const reordered = [...questions];
        const [movedQuestion] = reordered.splice(currentIndex, 1);
        reordered.splice(newIndex, 0, movedQuestion);

        // Update order values
        const updates = reordered.map((q, index) => ({
            id: q.id,
            order: index + 1
        }));

        try {
            // Update on backend
            for (const update of updates) {
                await fetch(`/api/questions/${update.id}/`, {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCSRFtoken(),
                    },
                    body: JSON.stringify({ order: update.order })
                });
            }

            setQuestions(reordered);
            toast.showSuccess('Success', 'Question order updated');
        } catch (error) {
            toast.showError('Error', 'Failed to reorder questions');
        }
    };

    const moveChoice = async (questionId, choiceId, direction) => {
        const question = questions.find(q => q.id === questionId);
        const currentIndex = question.choices.findIndex(c => c.id === choiceId);

        if (
            (direction === 'up' && currentIndex === 0) ||
            (direction === 'down' && currentIndex === question.choices.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        const reordered = [...question.choices];
        const [movedChoice] = reordered.splice(currentIndex, 1);
        reordered.splice(newIndex, 0, movedChoice);

        // Update order values
        const updates = reordered.map((c, index) => ({
            id: c.id,
            order: index + 1
        }));

        try {
            // Update on backend
            for (const update of updates) {
                await fetch(`/api/choices/${update.id}/`, {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCSRFtoken(),
                    },
                    body: JSON.stringify({ order: update.order })
                });
            }

            // Update local state
            setQuestions(questions.map(q =>
                q.id === questionId ? { ...q, choices: reordered } : q
            ));
            toast.showSuccess('Success', 'Choice order updated');
        } catch (error) {
            toast.showError('Error', 'Failed to reorder choices');
        }
    };

    const toggleQuestionSelection = (questionId) => {
        setSelectedQuestions(prev =>
            prev.includes(questionId)
                ? prev.filter(id => id !== questionId)
                : [...prev, questionId]
        );
    };

    const toggleAllQuestions = () => {
        if (selectedQuestions.length === questions.length) {
            setSelectedQuestions([]);
        } else {
            setSelectedQuestions(questions.map(q => q.id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedQuestions.length === 0) return;

        confirm({
            title: 'Delete Selected Questions',
            message: `Are you sure you want to delete ${selectedQuestions.length} question(s)? This action cannot be undone.`,
            confirmText: 'Delete All',
            onConfirm: async () => {
                try {
                    for (const questionId of selectedQuestions) {
                        await fetch(`/api/questions/${questionId}/`, {
                            method: 'DELETE',
                            credentials: 'include',
                            headers: {
                                'X-CSRFToken': getCSRFtoken(),
                            },
                        });
                    }
                    setQuestions(questions.filter(q => !selectedQuestions.includes(q.id)));
                    setSelectedQuestions([]);
                    toast.showSuccess('Success', `${selectedQuestions.length} question(s) deleted`);
                } catch (error) {
                    toast.showError('Error', 'Failed to delete some questions');
                }
            }
        });
    };

    const handleBulkDuplicate = async () => {
        if (selectedQuestions.length === 0) return;

        try {
            const duplicated = [];
            for (const questionId of selectedQuestions) {
                const question = questions.find(q => q.id === questionId);

                // Create duplicate question
                const questionResponse = await fetch(`/api/questions/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCSRFtoken(),
                    },
                    body: JSON.stringify({
                        text: `${question.text} (Copy)`,
                        poll: pollId,
                        order: questions.length + duplicated.length + 1
                    })
                });

                if (questionResponse.ok) {
                    const newQuestion = await questionResponse.json();

                    // Duplicate choices
                    for (const choice of question.choices) {
                        await fetch(`/api/choices/`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-CSRFToken': getCSRFtoken(),
                            },
                            body: JSON.stringify({
                                text: choice.text,
                                question: newQuestion.id,
                                order: choice.order
                            })
                        });
                    }

                    // Fetch the complete question with choices
                    const completeResponse = await fetch(`/api/questions/${newQuestion.id}/`, {
                        credentials: 'include',
                    });
                    const completeQuestion = await completeResponse.json();
                    duplicated.push(completeQuestion);
                }
            }

            setQuestions([...questions, ...duplicated]);
            setSelectedQuestions([]);
            toast.showSuccess('Success', `${duplicated.length} question(s) duplicated`);
        } catch (error) {
            toast.showError('Error', 'Failed to duplicate questions');
        }
    };

    if (!poll) {
        return (
            <Box bg={bgColor} minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
                <LoadingSpinner minHeight="400px" />
            </Box>
        );
    }

    return (
        <Box bg={bgColor} minHeight="100vh">
            <ConfirmDialogComponent />
            <PreflightModal
                isOpen={isPreflightOpen}
                onClose={onPreflightClose}
                pollId={pollId}
                onProceed={(data) => setPoll(data.poll)}
            />
            <SurveyPreview
                isOpen={isPreviewOpen}
                onClose={onPreviewClose}
                poll={poll}
                questions={questions}
            />
            <PreviewPollModal
                isOpen={isFullPreviewOpen}
                onClose={onFullPreviewClose}
                poll={poll}
                questions={questions}
                invitationText={poll?.text_invitation}
            />
            <ResponseTemplates
                isOpen={isTemplatesOpen}
                onClose={onTemplatesClose}
                onSelectTemplate={handleApplyTemplate}
            />
            <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
                <PageHeader
                    title={poll.title}
                    breadcrumbs={[
                        { label: 'Home', path: '/' },
                        { label: 'Polls', path: '/pollsPage' },
                        { label: poll.title }
                    ]}
                    subtitle={
                        <HStack>
                            <Text fontSize="sm" color="gray.600">Status:</Text>
                            <Badge colorScheme={getStateBadgeColor(poll.state)} fontSize="sm">
                                {poll.state.toUpperCase()}
                            </Badge>
                        </HStack>
                    }
                />

                {poll.description && (
                    <Text fontSize="md" color="gray.700" mb={spacing.group}>
                        {poll.description}
                    </Text>
                )}

                <Box
                    p={spacing.group}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="border.default"
                    bg="white"
                    mb={spacing.section}
                >
                    {editState.editingPoll ? (
                        <EditPollForm
                            poll={poll}
                            onSave={handleSavePoll}
                            onCancel={() => dispatch({ type: 'CANCEL_EDIT_POLL' })}
                        />
                    ) : (
                        <VStack align="stretch" spacing={spacing.item}>
                            <HStack spacing={spacing.compact} flexWrap="wrap">
                                <Button onClick={() => dispatch({ type: 'START_EDIT_POLL' })} size="sm" mr={2}>
                                    Edit Poll
                                </Button>
                                <Tooltip label="Preview Survey (Ctrl+P) - Opens in new window" placement="top">
                                    <Button
                                        leftIcon={<ViewIcon />}
                                        onClick={() => window.open(`/preview-poll/${pollId}`, '_blank', 'width=500,height=900,scrollbars=yes')}
                                        size="sm"
                                        colorScheme="teal"
                                        isDisabled={questions.length === 0}
                                    >
                                        Preview Survey
                                    </Button>
                                </Tooltip>
                                {(poll.state === 'staged' || poll.state === 'live') && (
                                    <Button
                                        onClick={() => window.open(`/preview-poll/${pollId}?withInvitation=true`, '_blank', 'width=500,height=900,scrollbars=yes')}
                                        size="sm"
                                        colorScheme="purple"
                                        variant="outline"
                                        isDisabled={questions.length === 0}
                                    >
                                        Preview Invitation
                                    </Button>
                                )}
                                <Button onClick={handleDeletePoll} {...buttonStyles.danger}>
                                    Delete Poll
                                </Button>
                            </HStack>

                            {/* Poll State Management */}
                            {poll.state === 'draft' && (
                                <HStack spacing={spacing.compact} flexWrap="wrap">
                                    <Button
                                        as={ReactRouterLink}
                                        to="/stagingpoll"
                                        state={{ selectedPollId: pollId }}
                                        colorScheme="blue"
                                        size="sm"
                                    >
                                        Stage Poll
                                    </Button>
                                    <Text fontSize="sm" color="gray.600">
                                        Configure audience and dates to stage this poll for deployment.
                                    </Text>
                                </HStack>
                            )}

                            {poll.state === 'staged' && (
                                <HStack spacing={spacing.compact} flexWrap="wrap">
                                    <Button onClick={handleSetPollLive} colorScheme="green" size="sm">
                                        Set Live
                                    </Button>
                                    <Text fontSize="sm" color="gray.600">
                                        Ready to go live. You can generate tranches after setting live.
                                    </Text>
                                </HStack>
                            )}

                            {poll.state === 'live' && (
                                <HStack spacing={spacing.compact} flexWrap="wrap">
                                    <Button
                                        as={ReactRouterLink}
                                        to={`/live-poll/${pollId}`}
                                        colorScheme="blue"
                                        size="sm"
                                    >
                                        Live Dashboard
                                    </Button>
                                    <Button
                                        as={ReactRouterLink}
                                        to={`/analytics/${pollId}`}
                                        colorScheme="purple"
                                        size="sm"
                                    >
                                        Analytics
                                    </Button>
                                    <Button onClick={handleCompletePoll} colorScheme="orange" size="sm">
                                        Complete Poll
                                    </Button>
                                </HStack>
                            )}

                            {poll.state === 'closed' && (
                                <HStack spacing={spacing.compact} flexWrap="wrap">
                                    <Button
                                        as={ReactRouterLink}
                                        to={`/analytics/${pollId}`}
                                        colorScheme="purple"
                                        size="sm"
                                    >
                                        View Analytics
                                    </Button>
                                    <Text fontSize="sm" color="gray.600">
                                        This poll is closed and no longer accepting responses.
                                    </Text>
                                </HStack>
                            )}
                        </VStack>
                    )}
                </Box>

                {/* Survey Page Designer - Organization comes FIRST */}
                <SurveyPageDesigner pollId={pollId} />

                {/* Create/Edit Questions - Content creation comes SECOND */}
                <Heading as="h2" size="lg" mb={spacing.group}>
                    Questions & Choices
                </Heading>

                {/* Add Question Input */}
                <VStack align="stretch" spacing={spacing.item} mb={spacing.group}>
                    <Input
                        value={newQuestion}
                        onChange={e => setNewQuestion(e.target.value)}
                        placeholder="New question text..."
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && newQuestion.trim()) {
                                handleAddQuestion();
                            }
                        }}
                    />
                    <HStack spacing={spacing.compact}>
                        <Button
                            onClick={handleAddQuestion}
                            colorScheme="teal"
                            size="sm"
                            isLoading={submitting}
                            loadingText="Adding..."
                        >
                            Add Question
                        </Button>
                        {questions.length > 0 && (
                            <>
                                <Button
                                    onClick={toggleAllQuestions}
                                    size="sm"
                                    variant="outline"
                                >
                                    {selectedQuestions.length === questions.length ? 'Deselect All' : 'Select All'}
                                </Button>
                                {selectedQuestions.length > 0 && (
                                    <>
                                        <Button
                                            leftIcon={<CopyIcon />}
                                            onClick={handleBulkDuplicate}
                                            size="sm"
                                            colorScheme="blue"
                                        >
                                            Duplicate ({selectedQuestions.length})
                                        </Button>
                                        <Button
                                            leftIcon={<DeleteBulkIcon />}
                                            onClick={handleBulkDelete}
                                            size="sm"
                                            colorScheme="red"
                                        >
                                            Delete ({selectedQuestions.length})
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                    </HStack>
                </VStack>

                {/* Questions List for Editing Only */}
                {questions.length === 0 ? (
                    <EmptyState message="No questions yet. Add your first question above to get started." />
                ) : (
                    <VStack align="stretch" spacing={spacing.compact}>
                        {questions.map((question, index) => (
                        <Box
                            key={question.id}
                            border="1px solid"
                            borderColor="border.default"
                            borderRadius="md"
                            bg="white"
                        >
                            {/* Question Header */}
                            <HStack spacing={spacing.compact} p={spacing.item} borderBottom="1px solid" borderColor="border.light">
                                <Checkbox
                                    isChecked={selectedQuestions.includes(question.id)}
                                    onChange={() => toggleQuestionSelection(question.id)}
                                    colorScheme="teal"
                                />
                                <Box flex="1">
                                    {editState.editingQuestionId === question.id ? (
                                        <EditQuestionForm
                                            question={question}
                                            onSave={(data) => handleSaveQuestion(question.id, data)}
                                            onCancel={() => dispatch({ type: 'CANCEL_EDIT_QUESTION' })}
                                        />
                                    ) : (
                                        <Heading as="h3" size="md">
                                            {question.text}
                                        </Heading>
                                    )}
                                </Box>
                                <Badge colorScheme="blue">{question.choices.length} choices</Badge>
                                <IconButton
                                    icon={<ChevronUpIcon />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Move up"
                                    onClick={() => moveQuestion(question.id, 'up')}
                                    isDisabled={index === 0}
                                />
                                <IconButton
                                    icon={<ChevronDownIcon />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Move down"
                                    onClick={() => moveQuestion(question.id, 'down')}
                                    isDisabled={index === questions.length - 1}
                                />
                            </HStack>

                            {/* Choices Section */}
                            <VStack align="stretch" spacing={spacing.compact} p={spacing.item} bg="background.shade1">
                                <HStack justify="space-between">
                                    <Text fontSize="sm" fontWeight="bold" color="gray.600">Choices</Text>
                                    <HStack spacing={spacing.compact}>
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            color="blue.500"
                                            onClick={() => dispatch({ type: 'START_EDIT_QUESTION', payload: question.id })}
                                        >
                                            Edit Question
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            color="red.500"
                                            onClick={() => handleDeleteQuestion(question.id)}
                                        >
                                            Delete Question
                                        </Button>
                                    </HStack>
                                </HStack>

                                {question.choices.map((choice, choiceIndex) => (
                                    <HStack key={choice.id} spacing={spacing.compact}>
                                        <IconButton
                                            icon={<ChevronUpIcon />}
                                            size="xs"
                                            variant="ghost"
                                            aria-label="Move choice up"
                                            onClick={() => moveChoice(question.id, choice.id, 'up')}
                                            isDisabled={choiceIndex === 0}
                                        />
                                        <IconButton
                                            icon={<ChevronDownIcon />}
                                            size="xs"
                                            variant="ghost"
                                            aria-label="Move choice down"
                                            onClick={() => moveChoice(question.id, choice.id, 'down')}
                                            isDisabled={choiceIndex === question.choices.length - 1}
                                        />
                                        {editState.editingChoiceId === choice.id ? (
                                            <Box flex="1">
                                                <EditChoiceForm
                                                    choice={choice}
                                                    onSave={(data) => handleSaveChoice(choice.id, data)}
                                                    onCancel={() => dispatch({ type: 'CANCEL_EDIT_CHOICE' })}
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                flex="1"
                                                p={spacing.compact}
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="gray.300"
                                                bg="white"
                                                cursor="pointer"
                                                onClick={() => dispatch({ type: 'START_EDIT_CHOICE', payload: choice.id })}
                                                _hover={{ bg: 'gray.50' }}
                                            >
                                                {choice.text}
                                            </Box>
                                        )}
                                    </HStack>
                                ))}

                                {/* Add New Choice */}
                                <Box mt={spacing.item} pt={spacing.item} borderTop="1px solid" borderColor="gray.200">
                                    <Input
                                        value={newChoice}
                                        onChange={e => setNewChoice(e.target.value)}
                                        placeholder="New choice text..."
                                        size="sm"
                                        mb={spacing.compact}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newChoice.trim()) {
                                                handleAddChoice(question.id);
                                            }
                                        }}
                                    />
                                    <HStack spacing={spacing.compact}>
                                        <Button onClick={() => handleAddChoice(question.id)} colorScheme="teal" size="sm">
                                            Add Choice
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setTemplateQuestionId(question.id);
                                                onTemplatesOpen();
                                            }}
                                            variant="outline"
                                            colorScheme="purple"
                                            size="sm"
                                        >
                                            Use Template
                                        </Button>
                                    </HStack>
                                </Box>
                            </VStack>
                        </Box>
                        ))}
                    </VStack>
                )}
            </Box>
        </Box>
    );
}

export default ManageQuestions;